
import React, { useState, useEffect } from "react"; // Added useEffect
import { Provider } from "@/types/supabase";
import type { Territory } from '@/types/supabase';
import { useIsMobile } from "@/hooks/use-mobile";

import SearchBar from "@/components/explore/SearchBar";
import FilterBar from "@/components/explore/FilterBar";
import ProvidersSorting from "@/components/explore/ProvidersSorting";
import ProvidersListWithAds from "@/components/explore/ProvidersListWithAds";
import ExploreHeader from "@/components/explore/ExploreHeader";
import PageHero from "@/components/layout/PageHero";

interface ExploreContentProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  activeFilters: {
    categories: string[];
    suburbs: string[];
    rating: string;
    verifiedOnly: boolean;
    showFavorites: boolean;
    topRatedOnly: boolean;
    featuredOnly: boolean;
  };
  setActiveFilters: React.Dispatch<React.SetStateAction<{
    categories: string[];
    suburbs: string[];
    rating: string;
    verifiedOnly: boolean;
    showFavorites: boolean;
    topRatedOnly: boolean;
    featuredOnly: boolean;
  }>>;
  appliedFilters: {
    categories: string[];
    suburbs: string[];
    rating: string;
    verifiedOnly: boolean;
    showFavorites: boolean;
    topRatedOnly: boolean;
    featuredOnly: boolean;
  };
  setAppliedFilters: React.Dispatch<React.SetStateAction<{
    categories: string[];
    suburbs: string[];
    rating: string;
    verifiedOnly: boolean;
    showFavorites: boolean;
    topRatedOnly: boolean;
    featuredOnly: boolean;
  }>>;
  handleSearch: (e: React.FormEvent) => void;
  handleResetFilters: () => void;
  sortOption: string;
  setSortOption: (option: string) => void;
  filteredProviders: Provider[];
  isLoading: boolean;
  allCategories: string[];
  territories: Territory[];
}

const ExploreContent = ({
  searchQuery,
  setSearchQuery,
  activeFilters,
  setActiveFilters,
  appliedFilters,
  setAppliedFilters,
  handleSearch,
  handleResetFilters,
  sortOption,
  setSortOption,
  filteredProviders,
  isLoading,
  allCategories,
  territories
}: ExploreContentProps) => {
  const isMobile = useIsMobile();
  
  // Calculate columns based on screen size
  const getColumnsPerRow = React.useCallback(() => {
    if (isMobile) return 1; // mobile: 1 column
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) return 3; // lg: 3 columns
    return 2; // md: 2 columns (default for non-mobile, non-lg)
  }, [isMobile]);
  
  const [columnsPerRow, setColumnsPerRow] = useState(getColumnsPerRow());

  const [displayCount, setDisplayCount] = useState(() => {
    const initialColumns = getColumnsPerRow();
    return initialColumns * 7; // Start with 7 rows
  });

  // Update columnsPerRow when isMobile or window size changes
  useEffect(() => {
    setColumnsPerRow(getColumnsPerRow());
  }, [isMobile, getColumnsPerRow]);


  const handleLoadMore = () => {
    const currentColumns = getColumnsPerRow();
    setDisplayCount(prev => prev + (currentColumns * 7)); // Add 7 more rows
  };

  // Reset display count when filters change or screen size changes
  React.useEffect(() => {
    const currentColumns = getColumnsPerRow();
    setDisplayCount(currentColumns * 7); // Reset to 7 rows
  }, [appliedFilters, searchQuery, sortOption, isMobile, getColumnsPerRow]);

  // Listen for resize events to adjust display count and columnsPerRow
  React.useEffect(() => {
    const handleResize = () => {
      const currentColumns = getColumnsPerRow();
      setColumnsPerRow(currentColumns);
      // Ensure displayCount is a multiple of columns, maintaining roughly the same number of rows.
      // Only update displayCount if columnsPerRow actually changed to prevent potential small loops if displayCount itself affects getColumnsPerRow indirectly.
      if (columnsPerRow !== currentColumns) {
        const currentRows = Math.ceil(displayCount / (columnsPerRow || currentColumns)); // Use currentColumns if columnsPerRow is 0 (initial state)
        setDisplayCount(currentColumns * currentRows);
      }
    };

    window.addEventListener('resize', handleResize);
    // Initial call to set correct display count based on columns
    handleResize(); 
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile, getColumnsPerRow, displayCount, columnsPerRow]); // Added columnsPerRow to dependencies

  const displayedProviders = filteredProviders.slice(0, displayCount);
  const hasMore = displayCount < filteredProviders.length;

  return (
    <div className="bg-brand-mist" data-test-responsive="explore-root">
      <PageHero
        title={<ExploreHeader title="Explore Services" />}
        description="Find Hong Kong service providers by service, area, rating, and saved favourites."
        contentClassName="py-14 md:py-16"
      >
        <div className="max-w-5xl">
          <SearchBar
            data-test-responsive="explore-search"
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            handleSearch={handleSearch}
          />

          <FilterBar
            data-test-responsive="explore-filters"
            searchQuery={searchQuery}
            allCategories={allCategories}
            territories={territories}
            setActiveFilters={setActiveFilters}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            handleResetFilters={handleResetFilters}
          />
        </div>
      </PageHero>

      <div className="container mx-auto px-4 py-8 md:py-10" data-test-responsive="explore-container">
        <div>
          <ProvidersSorting 
            data-test-responsive="explore-sorting"
            sortOption={sortOption}
            setSortOption={setSortOption}
            filteredProvidersCount={filteredProviders.length}
            loading={isLoading}
          />
          
          <ProvidersListWithAds 
            data-test-responsive="explore-providers-list"
            providers={displayedProviders}
            isLoading={isLoading}
            searchQuery={searchQuery}
            selectedCategory={appliedFilters.categories[0]}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            columnsPerRow={columnsPerRow}
          />
        </div>
      </div>
    </div>
  );
};

export default ExploreContent;
